import _ from 'underscore';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { useOnceEffect, getSharedValues } from './ab-testing-event-log';
import {
  CUSTOMER_JOURNEY_INTERFACE,
  CUSTOMER_JOURNEY_PRODUCT,
  CUSTOMER_JOURNEY_SUB_PRODUCT,
  userAgent,
} from './customer-journey-provider';

export const SIGNUP_FLOW = {
  NONE: null,
  Smart: 'Smart',
  UP: 'UP',
  SubscriptionAds: 'SubscriptionAds',
  NewExpert: 'NewExpert',
  GoogleImport: 'GoogleImport',
  FacebookImport: 'FacebookImport',
  SubscriptionAds2: 'SubscriptionAds2',
  StreamlinedSignup: 'StreamlinedSignup',
  PmaxLiteSignup: 'PmaxLiteSignup',
  M365: 'M365',
  MSAdsSDK: 'MSAdsSDK',
};

export const SIGNUP_SUB_FLOW = {
  NONE: null,
  GoogleImport: 'GoogleImport',
  FacebookImport: 'FacebookImport',
  SkipCampaign: 'SkipCampaign',
  CampaignCreation: 'CampaignCreation',
  SmartPage: 'SmartPage',
};

export const SIGNUP_PAGE_NONE = null;

const NoOpContext = {
  signupComplete: _.noop,
  changedChannel: _.noop,
  enteredPage: _.noop,
};

export const SignupFlowContext = React.createContext(NoOpContext);

function usePrevious(value) {
  const cache = useRef(value);

  useEffect(() => {
    cache.current = value;
  }, [value]);

  return cache.current;
}

function useStateWithPrevious(initialValue) {
  const [state, setState] = useState(initialValue);
  const cache = useRef({});

  const updateState = useCallback((newValue) => {
    const value = {
      Flow: newValue.Flow || SIGNUP_FLOW.NONE,
      SubFlow: newValue.SubFlow || SIGNUP_SUB_FLOW.NONE,
      Page: newValue.Page || SIGNUP_PAGE_NONE,
    };
    setState((prev) => {
      if (!_.isEqual(prev, value)) {
        cache.current = prev;
        return value;
      }
      return prev;
    });
  }, []);

  return [state, updateState, cache.current];
}

export function SignupFlowProvider(props) {
  const {
    children,
    serverContext,
    postEventLog,
    logWarning,
    deviceInfo,
    preCIDFlights,
    flights,
    postFlightLog,
    product,
    subProduct,
    customerInterface,
  } = props;

  // logging is asynchronously created on app launch
  // therefore logWarning and postEventLog are optional, however will disable shouldPost
  const shouldPost = props.shouldPost && _.isFunction(logWarning) && _.isFunction(postEventLog);
  const shouldPostFlights = props.shouldPost && _.isFunction(postFlightLog);
  const { CurrentAccount } = serverContext || {};

  const {
    CurrentAccount: prevAccount,
  } = usePrevious(serverContext) || {};

  const [{
    Flow,
    SubFlow,
    Page,
  }, setContext, prevContext] = useStateWithPrevious({
    Flow: SIGNUP_FLOW.NONE,
    SubFlow: SIGNUP_SUB_FLOW.NONE,
    Page: SIGNUP_PAGE_NONE,
  });

  // We don't want to re-log events if "eventValues" or "postEventLog" changes
  // therefore store both values as ref
  const eventValues = useRef({});
  const postEventLogRef = useRef({});
  const sharedValues = getSharedValues(serverContext);
  const UniqueId = sharedValues.SessionId;
  eventValues.current = {
    Mode: sharedValues.Mode,
    CID: sharedValues.CID,
    AID: sharedValues.AID,
    PageId: sharedValues.PageTrackingId,
  };
  const isPreCIDCreation = !sharedValues.CID;

  postEventLogRef.current = {
    postEvent: (EventName, EventValue) => postEventLog({
      UniqueId,
      EventName: `SignupFlow:${EventName}`,
      EventValue,
    }),
    logValidationError: (error) => {
      const message = `SignupFlowProvider validation error: ${error}`;
      logWarning(message);
    },
    postFlight: postFlightLog,
  };


  useOnceEffect(() => {
    if (shouldPost) {
      postEventLogRef.current.postEvent('AppLoad', {
        ...eventValues.current,
        deviceInfo,
        UserRegion: sharedValues.UserRegion,
        HashedUserName: sharedValues.HashedUserName,
      });
      return true;
    }
    return false;
  });

  useOnceEffect(() => {
    if (shouldPost && Flow !== SIGNUP_FLOW.NONE) {
      postEventLogRef.current.postEvent('StartedFlow', {
        ...eventValues.current,
        Flow,
      });
      return true;
    }
    return false;
  });

  useOnceEffect(() => {
    if (shouldPostFlights) {
      let allflights = flights;
      if (isPreCIDCreation) {
        allflights = [
          ...allflights,
          ...preCIDFlights,
        ];
      }
      postEventLogRef.current.postFlight(allflights);

      return true;
    }
    return false;
  });

  const signupComplete = useCallback((HasPayment, details) => {
    /*
    Needs to be added anywhere in signup where:
      1. Account is patched with: PreferredBillToPaymentInstrumentId: paymentId
      2. Account is patched with: LifecycleStatus: 29,
         aka LifecycleStatus: AccountLifeCycleStatus.Pending,
    */
    postEventLogRef.current.postEvent('SignupComplete', {
      ...eventValues.current,
      Flow,
      SubFlow,
      Page,
      HasPayment: !!HasPayment,
      ..._.pick(details, value => !_.isUndefined(value)),
    });
  }, [Flow, SubFlow, Page]);

  const changedChannel = useCallback(({ Before, After }) => {
    postEventLogRef.current.postEvent('ChangedChannel', {
      ...eventValues.current,
      Flow,
      Page,
      Before,
      After,
    });
  }, [Flow, Page]);


  useEffect(() => {
    if (!shouldPost || Flow === SIGNUP_FLOW.NONE) {
      return;
    }

    if (
      (prevContext.Flow !== Flow || prevContext.SubFlow !== SubFlow || prevContext.Page !== Page) &&
      Page !== SIGNUP_PAGE_NONE
    ) {
      postEventLogRef.current.postEvent('EnteredPage', {
        ...eventValues.current,
        Flow,
        SubFlow,
        Page,
      });
    }

    if (prevContext.Flow !== Flow && prevContext.Flow !== SIGNUP_FLOW.NONE) {
      if (prevContext.Page === Page) {
        postEventLogRef.current.logValidationError('page should have been modified when flow changed');
      }

      postEventLogRef.current.postEvent('SwitchedFlow', {
        CID: eventValues.current.CID,
        AID: eventValues.current.AID,
        FromFlow: prevContext.Flow,
        ToFlow: Flow,
        FromPage: prevContext.Page || null,
        ToPage: Page || null,
      });
    }
  }, [Flow, Page, SubFlow, prevContext.Flow, prevContext.Page, prevContext.SubFlow, shouldPost]);

  useEffect(() => {
    if (!shouldPost) {
      return;
    }

    const prevHasAID = !!(prevAccount && prevAccount.Id);
    const nowHasAID = !!(CurrentAccount && CurrentAccount.Id);

    if (prevHasAID && prevAccount.AccountMode !== CurrentAccount.AccountMode) {
      postEventLogRef.current.postEvent('UpdatedMode', {
        CID: eventValues.current.CID,
        AID: eventValues.current.AID,
        Flow,
        Page,
        FromMode: prevAccount.AccountMode,
        ToMode: CurrentAccount.AccountMode,
      }, {});
    }

    if (!prevHasAID && nowHasAID) {
      postEventLogRef.current.postEvent('CreatedCID', {
        ...eventValues.current,
        Flow,
        Page,
        Product: product,
        SubProduct: subProduct,
        Interface: customerInterface,
        OS: userAgent,
        ResolutionWidth: _.get(window, 'innerWidth'),
        ResolutionHeight: _.get(window, 'innerHeight'),
        PixelRatio: _.get(window, 'devicePixelRatio'),
      }, {});
    }
  }, [CurrentAccount, Flow, Page, customerInterface, prevAccount, product, shouldPost, subProduct]);

  return (
    <SignupFlowContext.Provider value={shouldPost ? {
      enteredPage: setContext,
      signupComplete,
      changedChannel,
    } : NoOpContext}
    >
      {children}
    </SignupFlowContext.Provider>
  );
}

SignupFlowProvider.propTypes = {
  shouldPost: PropTypes.bool,
  logWarning: PropTypes.func,
  postEventLog: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  serverContext: PropTypes.shape({}),
  deviceInfo: PropTypes.string,
  flights: PropTypes.arrayOf(PropTypes.shape({})),
  preCIDFlights: PropTypes.arrayOf(PropTypes.shape({})),
  postFlightLog: PropTypes.func,
  product: PropTypes.string,
  subProduct: PropTypes.string,
  customerInterface: PropTypes.string,
};

SignupFlowProvider.defaultProps = {
  shouldPost: false,
  serverContext: undefined,
  logWarning: null,
  postEventLog: null,
  deviceInfo: null,
  flights: [],
  preCIDFlights: [],
  postFlightLog: null,
  product: CUSTOMER_JOURNEY_PRODUCT.None,
  subProduct: CUSTOMER_JOURNEY_SUB_PRODUCT.None,
  customerInterface: CUSTOMER_JOURNEY_INTERFACE.Web,
};
